import classNames from 'classnames'
import { MyIcon } from '~/components/ui/MyIcon'
import { pattern2Header } from '~/configs/ai'
import copy from 'copy-to-clipboard'
import styles from './CopyParams.module.css'
import { useMutation } from '@apollo/client'
import {
  AiArtworkShareSource,
  AiCreateArtworkModel,
  createShareCode,
  createShareCodeVariables,
  getAIArtWork_pictureGraph_aiArtwork,
  modelOptions,
  Post_AiArtworkInfo,
  SuperType,
} from '~/graphqls/types'
import { CREATE_SHARE_CODE } from '~/graphqls/gqls'
import { useValidateAuth } from '~/hooks/useValidateAuth'
import { message, Modal } from 'antd'
import { graphQLErrorMessage, trackEvent } from '~/utils'
import accurateCalc from 'accurate-core'

const CopyParams: React.FC<{
  product: getAIArtWork_pictureGraph_aiArtwork | Post_AiArtworkInfo
  validateYellow?: () => boolean
  source?: AiArtworkShareSource
  title?: string
  className?: string
  children?: React.ReactNode
}> = ({
  product,
  validateYellow = () => true,
  source = AiArtworkShareSource.AI_ARTWORK,
  title,
  children,
  className,
}) => {
  const { validateIsLogin } = useValidateAuth()
  const [genCodeAction] = useMutation<
    createShareCode,
    createShareCodeVariables
  >(CREATE_SHARE_CODE)
  const copyParams = async () => {
    try {
      if (!validateIsLogin()) return
      if (!validateYellow?.()) return

      const res = await genCodeAction({
        variables: {
          aiArtworkId: product.id,
          source,
        },
      })
      const controlTitle =
        pattern2Header[product.pattern! as keyof typeof pattern2Header]?.enTitle
      const url = `${process.env.NEXT_PUBLIC_MOBILE_HOST}/s/${res?.data?.pictureMutation?.code}`
      const prompt = `咒语(Prompt): ${
        (product?.prompt as string)?.length > 250
          ? product.prompt?.slice(0, 250) + '...'
          : product.prompt
      }`

      const content =
        product.model === AiCreateArtworkModel.STYLE
          ? `${prompt}
Model: 风格模型${
              !!product.styleModelOption?.name
                ? ` (${product.styleModelOption?.name})`
                : ''
            }

${url}
一键复制后打开【无界AI】APP 或 点击链接 自动填入全部参数进行同款创作`
          : `${prompt}
${!!product?.modelTheme ? `Model: ${product?.modelTheme}` : ''}${
              !!product.cfg && product.superType !== SuperType.FOUR_K
                ? `, CFG scale: ${product.cfg}`
                : ''
            }${
              product.prefineInfo
                ? `, Hires upscale: ${product.prefineInfo.multiple}`
                : ''
            }${
              !!product?.characterOptions?.length
                ? `, PT: ${product?.characterOptions
                    ?.map(item => item.name)
                    ?.join('、')}`
                : ''
            }${
              !!product?.modelFusionOptions?.length
                ? `, LoRA: ${((product?.modelFusionOptions || []) as any[])
                    .concat(
                      product?.userTrainedModelOption?.map(it => ({
                        name: it.trainedModel?.modelName,
                        weight: it.weight,
                      })) || []
                    )
                    ?.map(item => `${item.name}(${item.weight})`)
                    ?.join('、')}`
                : ''
            }${
              !!controlTitle
                ? `, ControlNet Model: ${controlTitle}, ControlNet Guidance Strength: ${accurateCalc.divide(
                    Number(product?.creativityDegree),
                    100
                  )}`
                : ''
            }

${url}
一键复制后打开【无界AI】APP 或 点击链接 自动填入全部参数进行同款创作`

      Modal.info({
        title: (
          <div className={styles.copyModalTitle}>
            <div>
              <MyIcon icon="copy" />
              全参复制
            </div>
            <div>
              分享给好友，好友复制打开【无界AI】APP 或 点击链接
              自动填入全部参数进行同款创作~
            </div>
          </div>
        ),
        maskStyle: {
          backgroundColor: 'rgba(0,0,0,0.8)',
        },
        maskClosable: true,
        width: 375,
        className: 'hookModal dark',
        content: <div className={styles.copyModalContent}>{content}</div>,
        okText: '复制参数',
        onOk: close => {
          copy(content)
          trackEvent({
            category: '全参复制',
            label: '确认复制参数',
          })
          close()
        },
      })
      trackEvent({
        category: '全参复制',
        label: '全参复制',
      })
    } catch (error) {
      message.destroy()
      message.error(graphQLErrorMessage(error) || '复制失败')
    }
  }

  return (
    <div className={className} onClick={copyParams}>
      {children || (
        <div className="flex items-center font-medium text-neutral-400 hover:text-primary transition-colors cursor-pointer">
          <MyIcon icon="copy" className="mr-1" />
          <span>全参复制</span>
        </div>
      )}
    </div>
  )
}

export default CopyParams
